import * as Sentry from '@sentry/nextjs';
import axios from 'axios';

import { NOT_FOUND_CODE, UNAUTHENTICATED_CODE } from '~/common/constants/errorCodes';
import { getAuthorizationHeaderFromCookie } from '~/utils/auth';

import type { AxiosError, AxiosInstance } from 'axios';

export class RestApi {
  protected static handleError<T>(error: AxiosError): T | null {
    const status = error.response?.status;

    const EXPECTED_STATUS = new Set([UNAUTHENTICATED_CODE, NOT_FOUND_CODE]);

    if (EXPECTED_STATUS.has(status ?? 0) || RestApi.isRequestAbort(error)) {
      // Expected: unauthenticated, not found, or aborted/canceled requests
      return null;
    }

    Sentry.captureException(error);
    return null;
  }

  private static initialize(baseUrl: string | undefined): AxiosInstance {
    return axios.create({
      baseURL: baseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        ...getAuthorizationHeaderFromCookie(),
      },
    });
  }

  private static isRequestAbort(error: unknown): boolean {
    const axiosError = error as AxiosError & { request?: { aborted?: boolean } };

    return (
      axios.isCancel?.(axiosError) ||
      axiosError.code === 'ECONNABORTED' || // timeout
      axiosError.code === 'ERR_CANCELED' || // axios v1 canceled
      axiosError.code === 'ECONNRESET' || // connection reset by peer
      axiosError.request?.aborted === true || // http.ClientRequest aborted
      (typeof axiosError.message === 'string' && /aborted/i.test(axiosError.message))
    );
  }

  static setAuthorizationHeader(auth: Record<string, string>): void {
    this.snifferApi.defaults.headers.common.Authorization = auth.authorization;
    this.woofApi.defaults.headers.common.Authorization = auth.authorization;
  }

  protected static snifferApi: AxiosInstance = RestApi.initialize(
    process.env.NEXT_PUBLIC_SNIFFER_API_BASE_URL
  );

  protected static woofApi: AxiosInstance = RestApi.initialize(
    process.env.NEXT_PUBLIC_API_BASE_URL
  );
}
